// Guided page tours — the "▸ Show me" content, one step-list per page.
//
// Data, like content/map.ts (FLOW) or the onboarding steps: each tour is an
// array of ironvale WalkStep targeting REAL elements on the live page by their
// stable `data-tour="…"` attribute. The ShowMe trigger (components/ShowMe.tsx)
// looks the current route up here and only appears where a tour exists.
//
// Keep targets to elements that always render (headers, section wrappers, the
// primary CTA). Where a target is conditional (a list vs. its empty state), use
// a function target so the tour resolves whichever is on screen, and the
// Walkthrough skips it gracefully if neither is.

import type { WalkStep } from '@diwata/ironvale'

/** Match a page's route path to its tour. Longest-prefix wins (see tourForPath). */
export interface Tour {
  /** Route prefix this tour belongs to (e.g. '/home', '/domains'). */
  path: string
  /** What the header trigger says it will show. */
  label: string
  steps: WalkStep[]
}

const HOME_TOUR: Tour = {
  path: '/home',
  label: 'Show me Home',
  steps: [
    {
      target: '[data-tour="home-header"]',
      title: 'This is Home',
      body: 'Your whole verse at a glance. “How it connects” next to the title draws the loop everything follows.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="home-needs"]',
      title: 'What needs you',
      body: 'The one-to-three things awaiting your judgment sit up top. Each row is a single click to act on.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="home-tiles"]',
      title: 'Your live counts',
      body: 'Entities, relations, domains, signals, missions. Every tile is also the door into that surface.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="home-quick"]',
      title: 'Start something',
      body: 'Jump straight into the common first moves: a new domain, a new mission, or open the constellation.',
      placement: 'top',
    },
  ],
}

const DOMAINS_TOUR: Tour = {
  path: '/domains',
  label: 'Show me Domains',
  steps: [
    {
      target: '[data-tour="domains-header"]',
      title: 'Domains shape your world',
      body: 'A domain is one part of your world: a set of things you track and how they connect.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="domains-new"]',
      title: 'Build a new one',
      body: 'The guided builder suggests what usually lives in a domain, so you see what you get before you commit.',
      placement: 'bottom',
    },
    {
      // Conditional: the grid when you have domains, the empty state when you don't.
      target: () => document.querySelector('[data-tour="domains-list"]'),
      title: 'Your domains live here',
      body: 'Each card opens to inspect, edit, or delete. Click a name to step into that domain’s own page.',
      placement: 'top',
    },
    {
      target: '[data-tour="domains-templates"]',
      title: 'Or start from a template',
      body: 'Ready-made domains you can fork in one click, shelved by the sphere of life they belong to.',
      placement: 'top',
    },
  ],
}

const TOURS: Tour[] = [HOME_TOUR, DOMAINS_TOUR]

/**
 * The tour for a route, by longest matching path prefix (so '/domains/xyz'
 * still finds the Domains tour). Returns null where no tour exists — the
 * trigger then never renders.
 */
export function tourForPath(pathname: string): Tour | null {
  let best: Tour | null = null
  for (const t of TOURS) {
    if (pathname === t.path || pathname.startsWith(t.path + '/') || pathname.startsWith(t.path)) {
      if (!best || t.path.length > best.path.length) best = t
    }
  }
  return best
}
